<template>

  <el-dialog
    top="5vh"
    :visible.sync="isVisible"
    :width="'25%'"
    :custom-class="'panel-settings-dialog'"
    :title="'Panel configuration'"
  >
    <el-form label-position="top">

       <div class="ibox-content ibox-heading">
        Settings for interactions that show up in the panel.
      </div>
      <br>
      <el-tooltip class="item" effect="dark" content="Name of the panel" placement="bottom">
        <el-form-item label="Panel name">
          <el-input v-model="settings.name" :minlength="1" :maxlength="40" :disabled="panelType === 'onair'" />
        </el-form-item>
      </el-tooltip>

      <el-tooltip class="item" effect="dark" content="Set the panel layout size" placement="bottom">
        <el-form-item label="Column Layout">
          <el-radio-group v-model="settings.columns">
            <el-radio-button label="1">1 Column</el-radio-button>
            <el-radio-button label="2">2 Column</el-radio-button>
            <el-radio-button label="3">3 Column</el-radio-button>
          </el-radio-group>
        </el-form-item>
      </el-tooltip>

      <el-form-item label="Preload media">
        <el-tooltip class="item" effect="dark" content="Preload audio and video" placement="bottom">
          <el-radio-group v-model="settings.mediaPreload">
            <el-radio-button label="auto">On</el-radio-button>
            <el-radio-button label="none">Off</el-radio-button>
          </el-radio-group>
        </el-tooltip>
      </el-form-item>

      <el-form-item label="Panel Locker" v-show="panelType !== 'activity'">
        <el-tooltip class="item"
        effect="dark" content="Panel exit locker" placement="bottom">
          <el-radio-group v-model="settings.panelLock">
            <el-radio-button label="none">On</el-radio-button>
            <el-radio-button label="auto">Off</el-radio-button>
          </el-radio-group>
        </el-tooltip>
      </el-form-item>

      <el-form-item label="Panel Color">
        <el-tooltip class="item" effect="dark" content="Set a unique colour for your panel" placement="bottom">
          <el-color-picker v-model="settings.colour" />
        </el-tooltip>
      </el-form-item>

      <el-tooltip
        v-show="!isOnAirOrActivity"
        class="item"
        effect="dark"
        content="Select campaigns you want to get interactions from"
        placement="bottom"
      >
        <el-form-item>
          <span slot="label">
            Campaigns
            <el-tooltip content="Select All" placement="right">
              <el-checkbox v-model="isAllCampaignsChecked" :disabled="isOnAirOrActivity" />
            </el-tooltip>
          </span>
          <el-select
            v-model="settings.campaign_ids"
            multiple
            size="large"
            style="width:100%"
            placeholder="Select..."
            filterable
            collapse-tags
            :disabled="isOnAirOrActivity"
          >
            <el-option v-for="campaign in campaigns" :key="campaign.id" :label="campaign.title" :value="campaign.id" />
          </el-select>
        </el-form-item>
      </el-tooltip>

      <el-tooltip
        v-show="!isOnAirOrActivity"
        class="item"
        effect="dark"
        content="Select channels you want to get interactions from"
        placement="bottom"
      >
        <el-form-item>
          <span slot="label">
            Channels
            <el-tooltip content="Select All" placement="right">
              <el-checkbox v-model="isAllChannelsChecked" :disabled="isOnAirOrActivity" />
            </el-tooltip>
          </span>
          <el-select
            v-model="settings.channel_ids"
            size="large"
            style="width:100%"
            placeholder="Select..."
            multiple
            filterable
            collapse-tags
            :disabled="isOnAirOrActivity"
          >
            <el-option v-for="channel in activeChannels" :key="channel.id" :label="channel.title" :value="channel.id">
              <span style="float: left">{{ channel.title }}</span>
              <span style="float: right; color: #8492a6; font-size: 0.7em; margin-right: 1.6em">{{ channel.channel_type_title }}</span>
            </el-option>
          </el-select>
        </el-form-item>
      </el-tooltip>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="isVisible = false">Cancel</el-button>
      <el-button type="primary" :loading="updatingSettings" @click="saveSettings">Save Panel Settings</el-button>
    </span>

  </el-dialog>
</template>

<script>
export default {
  name: 'MessagePanelSettings',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '#ffffff',
    },
    columns: {
      type: String,
      default: '1',
    },
    panelType: {
      type: String,
      default: null,
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
    channels: {
      type: Array,
      default: () => [],
    },
    campaignIds: {
      type: Array,
      default: () => [],
    },
    channelIds: {
      type: Array,
      default: () => [],
    },
    mediaPreload: {
      type: String,
      default: 'auto',
    },
    panelLock: {
      type: String,
      default: 'auto',
    },
    updatingSettings: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    settings: {
      name: '',
      color: '',
      columns: '1',
      campaignIds: [],
      channelIds: [],
      mediaPreload: 'auto',
      panelLock: 'auto',
    },
  }),
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('setVisible', value);
      },
    },

    isOnAirOrActivity() {
      return (
        this.panelType === 'onair' ||
        this.panelType === 'activity' ||
        this.panelType === 'twittertrends'
      );
    },

    activeChannels() {
      return this.channels.filter(channel => channel.live);
    },

    isAllCampaignsChecked: {
      get() {
        return (
          this.settings.campaign_ids && this.settings.campaign_ids.length === this.campaigns.length
        );
      },
      set() {
        if (this.isAllCampaignsChecked) {
          this.settings.campaign_ids = [];
        } else {
          this.settings.campaign_ids = this.campaigns.map(x => x.id);
        }
      },
    },

    isAllChannelsChecked: {
      get() {
        return (
          this.settings.channel_ids && this.settings.channel_ids.length === this.channels.length
        );
      },
      set() {
        if (this.isAllChannelsChecked) {
          this.settings.channel_ids = [];
        } else {
          this.settings.channel_ids = this.channels.map(x => x.id);
        }
      },
    },
  },

  mounted() {
    this.syncSettings();
  },

  methods: {
    saveSettings() {
      this.$emit('saveSettings', this.settings);
    },

    syncSettings() {
      this.settings = {
        uuid: this.id,
        name: this.name,
        colour: this.color,
        columns: this.columns,
        campaign_ids: this.campaignIds,
        channel_ids: this.channelIds,
        mediaPreload: this.mediaPreload,
        panelLock: this.panelLock,
      };
    },
  },

  watch: {
    id() {
      this.syncSettings();
    },
  },
};
</script>

<style lang="scss" scoped>
.panel-settings-dialog {
  width: 400px;
}
.ibox-heading {
  border-width: 0px;
}
</style>



// WEBPACK FOOTER //
// MessagePanelSettings.vue?70fda5de